import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DataIntegrityError, DatabaseError } from "../auth/errors";

import { ViewFolderId, ViewId } from "../ids/definitions";

/**
 * Per-view persisted configuration for table columns: ordering and visibility.
 * - `order` is the user's preferred left-to-right column order (column IDs).
 *   `null` means the user has not customized order on this view; the client
 *   should use the table's default order. Unknown IDs are ignored; known IDs
 *   missing from `order` fall back to the table's default order on the end.
 * - `visibility` is keyed by column ID; missing entries default to visible.
 */
export const ViewColumnsConfig = Schema.Struct({
  order: Schema.NullOr(Schema.Array(Schema.String)),
  visibility: Schema.Record({ key: Schema.String, value: Schema.Boolean }),
});
export type ViewColumnsConfig = typeof ViewColumnsConfig.Type;

export const ViewResponse = Schema.Struct({
  id: ViewId,
  name: Schema.String,
  table: Schema.String,
  filters: Schema.Array(Schema.Unknown),
  filterGroup: Schema.NullOr(Schema.Unknown),
  columns: Schema.NullOr(ViewColumnsConfig),
  sort: Schema.NullOr(Schema.Unknown),
  pageSize: Schema.NullOr(Schema.Number),
  folderId: Schema.NullOr(ViewFolderId),
  position: Schema.Number,
  deleted: Schema.NullOr(Schema.Boolean),
  isNotEditable: Schema.optional(Schema.Boolean),
});
export type ViewResponse = typeof ViewResponse.Type;

export const FolderResponse = Schema.Struct({
  id: ViewFolderId,
  table: Schema.String,
  name: Schema.String,
  position: Schema.Number,
  isPrivate: Schema.Boolean,
});
export type FolderResponse = typeof FolderResponse.Type;

const GetViewsAndFoldersResponse = Schema.Struct({
  views: Schema.Array(ViewResponse),
  folders: Schema.Array(FolderResponse),
});

const CreateFolderInput = Schema.Struct({
  id: Schema.optional(ViewFolderId),
  name: Schema.String,
  viewIds: Schema.Array(Schema.String),
  isPrivate: Schema.Boolean,
});

const CreateFolderResponse = Schema.Struct({ folderId: ViewFolderId });

const UpdateFolderInput = Schema.Struct({
  name: Schema.String,
  viewIds: Schema.Array(Schema.String),
});

const SaveViewsAndFoldersInput = Schema.Struct({
  views: Schema.Array(ViewResponse),
  folders: Schema.Array(FolderResponse),
});

const SaveUserViewFolderOrderInput = Schema.Struct({
  items: Schema.Array(
    Schema.Struct({
      id: Schema.String,
      type: Schema.Literal("view", "folder"),
    }),
  ),
});

const CreateViewInput = Schema.Struct({
  id: Schema.optional(ViewId),
  name: Schema.String,
  filterGroup: Schema.NullOr(Schema.Unknown),
  folderId: Schema.NullOr(ViewFolderId),
  columns: Schema.optional(Schema.NullOr(ViewColumnsConfig)),
});

const CreateViewResponse = Schema.Struct({ viewId: ViewId });

const UpdateViewInput = Schema.Struct({
  name: Schema.String,
  filterGroup: Schema.NullOr(Schema.Unknown),
  folderId: Schema.NullOr(ViewFolderId),
  columns: Schema.optional(Schema.NullOr(ViewColumnsConfig)),
});

export const ViewFolderGroup = HttpApiGroup.make("viewFolder")
  .add(
    endpoint.merchant
      .get("getViewsAndFolders", "/view-folders")

      .addSuccess(GetViewsAndFoldersResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createFolder", "/view-folders")

      .setPayload(CreateFolderInput)
      .addSuccess(CreateFolderResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateFolder", "/view-folders/:id")

      .setPath(Schema.Struct({ id: ViewFolderId }))
      .setPayload(UpdateFolderInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteFolder", "/view-folders/:id")

      .setPath(Schema.Struct({ id: ViewFolderId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("saveViewsAndFolders", "/view-folders/reorder")

      .setPayload(SaveViewsAndFoldersInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("saveUserViewFolderOrder", "/view-folders/user-order")

      .setPayload(SaveUserViewFolderOrderInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createView", "/view-folders/views")

      .setPayload(CreateViewInput)
      .addSuccess(CreateViewResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateView", "/view-folders/views/:id")

      .setPath(Schema.Struct({ id: ViewId }))
      .setPayload(UpdateViewInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteView", "/view-folders/views/:id")

      .setPath(Schema.Struct({ id: ViewId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
